import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldAlertIcon } from 'lucide-react';
import { app, notes, task, type NoteAction, type NotesSnapshot } from '@/lib/bridge';
import { describeError } from '@/lib/errorMessage';
import { useAccounts } from '@/lib/useAccounts';
import { useAppInfo } from '@/lib/useAppInfo';
import { useAppReady } from '@/lib/useAppReady';
import { useGroupedSurface } from '@/lib/useGroupedSurface';
import { AccountCard } from '@/components/AccountCard';
import { PageShell } from '@/components/PageShell';
import { RowList } from '@/components/RowList';
import { SelectRow } from '@/components/SelectRow';
import { InfoCard, TaskProgressCard } from '@/components/task-ui';
import { PageSkeleton } from '@/components/skeletons';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CARD, FOOTNOTE } from '@/components/ui';
import { cn } from '@/lib/utils';

/**
 * 薯薯全能通 · 整账号批量操作（`/shushu/batch?accountId=&action=delete`）。
 *
 * 对照搬小书 `mobile/actions`：对**整个账号的线上笔记**执行，确认用底部弹层、破坏性操作要逐条勾选（三勾选）；
 * 进度显示成功 / 跳过 / 失败，限流 / 登录失效等停下时写明原因、可继续。
 * 不同的是循环在原生任务引擎里（边翻线上列表边执行、可暂停续跑、任务中心可见），页面只发起和展示。
 * 操作目录（文案、估算、勾选项）由原生 `notes.actions {scope: all}` 下发。
 */

/** `notes.actions {scope: all}` / `notes.run {scope: all}` 从 v5 起 */
const MIN_BRIDGE = 5;

export function ShushuBatchActionPage() {
  const [params] = useSearchParams();
  const actionKey = params.get('action') ?? 'delete';
  const { info, loading: infoLoading, isNative } = useAppInfo();
  const { accounts, selectedId: accountId, loading: accountsLoading, error: accountError } = useAccounts();
  const [action, setAction] = useState<NoteAction | null | undefined>(undefined);
  const [snapshot, setSnapshot] = useState<NotesSnapshot | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [checked, setChecked] = useState<boolean[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useGroupedSurface();

  const outdated = !!info && info.bridgeVersion < MIN_BRIDGE;
  const loaded = !infoLoading && !accountsLoading && (action !== undefined || outdated || !accountId || error !== null);
  useAppReady(loaded);

  const account = accounts.find((a) => a.id === accountId) ?? null;

  useEffect(() => {
    if (!accountId || outdated || infoLoading) return;
    let cancelled = false;
    Promise.all([notes.allActions(accountId), notes.state(accountId)])
      .then(([catalog, state]) => {
        if (cancelled) return;
        setAction(catalog.actions.find((a) => a.key === actionKey) ?? null);
        // 只接管整账号批量的快照；勾选批量的进度在列表 / 详情页看
        if (state.scope === 'all' && state.action === actionKey) setSnapshot(state);
      })
      .catch((e) => {
        if (!cancelled) setError(describeError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, actionKey, outdated, infoLoading, tick]);

  useEffect(
    () =>
      notes.onProgress((next) => {
        if (next.scope !== 'all' || (next as { accountId?: string }).accountId !== accountId) return;
        setSnapshot(next);
        if (next.state === 'running') return;
        void app.haptic(next.state === 'completed' && !next.failed ? 'success' : 'error');
        setTick((t) => t + 1);
      }),
    [accountId],
  );

  const running = snapshot?.state === 'running';
  const stopped = snapshot?.state === 'failed' || snapshot?.state === 'paused';
  const finished = snapshot?.state === 'completed';

  const openSheet = () => {
    if (!action) return;
    setChecked((action.checklist ?? []).map(() => false));
    setSheetOpen(true);
    void app.haptic('light');
  };

  const start = useCallback(async () => {
    if (!action || !accountId) return;
    setSheetOpen(false);
    setBusy(true);
    setError(null);
    try {
      setSnapshot(await notes.runAll(accountId, action.key));
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }, [accountId, action]);

  const resume = useCallback(async () => {
    if (!snapshot?.taskId) return;
    setBusy(true);
    setError(null);
    try {
      await task.resume(snapshot.taskId);
      setSnapshot({ ...snapshot, state: 'running', error: undefined });
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }, [snapshot]);

  const done = snapshot?.done ?? 0;
  const failed = snapshot?.failed ?? 0;
  const skipped = snapshot?.skipped ?? 0;
  const total = Math.max(snapshot?.total ?? 0, done + failed + skipped);

  return (
    <PageShell title={action?.label ?? '批量操作'} description="对整个账号的线上笔记执行">
      {!isNative && loaded ? (
        <Card className={CARD}>
          <p className="text-muted-foreground text-xs">
            当前不在 App 内。浏览器里单独调试请加 <code>?mock=1</code>。
          </p>
        </Card>
      ) : null}

      {outdated ? (
        <InfoCard title="需要更新 App" icon={<ShieldAlertIcon className="text-primary size-5 shrink-0" />}>
          当前 App 版本还不支持整账号批量操作，请更新后再试。
        </InfoCard>
      ) : null}

      {loaded && account ? <AccountCard account={account} showId={false} /> : null}

      {action === null && !outdated ? (
        <InfoCard title="暂不支持" icon={<ShieldAlertIcon className="text-primary size-5 shrink-0" />}>
          该平台还不支持这个操作。
        </InfoCard>
      ) : null}

      {snapshot && (running || stopped || finished) ? (
        <TaskProgressCard
          title={running ? `正在${action?.label ?? '处理'}` : stopped ? '已暂停' : '本次结果'}
          right={`${done + failed + skipped} / ${total}`}
          done={done + failed + skipped}
          total={total}
          finished={finished}
          paused={stopped}
          bar={!finished}
          action={
            <>
              <div className="text-muted-foreground flex gap-3.5 text-footnote tabular-nums">
                <span>成功 {done}</span>
                {skipped ? <span>跳过 {skipped}</span> : null}
                <span className={cn(failed > 0 && 'text-destructive')}>失败 {failed}</span>
              </div>
              <p className={cn('text-footnote leading-snug', stopped ? 'text-destructive' : 'text-muted-foreground')}>
                {running
                  ? `会逐页读取线上列表，一篇一篇处理${action?.destructive ? '；翻完一轮后会再核对一遍' : '，已是目标状态的跳过'}。留在 App 内即可，可以离开本页，进度在任务中心也能看到。`
                  : stopped
                    ? `${snapshot.error ? describeError(snapshot.error) : '已暂停'}。已处理的不会重复，继续后从停下的地方接着来。`
                    : `已完成，成功 ${done} 篇${skipped ? `，${skipped} 篇无需处理` : ''}${failed ? `，失败 ${failed} 篇（任务中心可看原因）` : ''}。`}
              </p>
              {stopped ? (
                <Button className="w-full" disabled={busy} onClick={() => void resume()}>
                  继续
                </Button>
              ) : null}
            </>
          }
        />
      ) : null}

      {action && !running && !stopped ? (
        <>
          <Card className={CARD}>
            <p className="text-body font-semibold">{action.label}</p>
            <p className="text-muted-foreground text-subheadline leading-normal">{action.confirm.message}</p>
            {action.destructive && action.unbacked ? (
              <p className="text-destructive text-subheadline leading-normal">
                其中 {action.unbacked} 篇本机没有备份，删除后无法找回。需要保留的话，先去「数据备份」备份。
              </p>
            ) : null}
          </Card>
          <Button
            variant={action.destructive ? 'destructive' : 'default'}
            className="w-full"
            disabled={busy || !!action.disabledReason}
            onClick={openSheet}
          >
            开始{action.label}
          </Button>
          <p className={FOOTNOTE}>{action.disabledReason ?? '每篇间隔约 1 秒；遇到限流、登录失效或连续失败会自动停下。'}</p>
        </>
      ) : null}

      {(error ?? accountError) ? <p className="text-destructive px-1 text-sm">{error ?? accountError}</p> : null}

      {!loaded ? <PageSkeleton kind="batch" /> : null}

      {/* 确认弹层（对照搬小书：破坏性操作逐条勾选，全部勾上才能开始） */}
      {sheetOpen && action ? (
        <div className="animate-in fade-in-0 fixed inset-0 z-50 flex items-end bg-black/40" onClick={() => setSheetOpen(false)}>
          <div
            className="animate-in slide-in-from-bottom bg-background w-full rounded-t-[20px] px-4 pt-5 pb-[calc(16px+env(safe-area-inset-bottom))] duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="mb-2 text-title3 font-semibold">{action.confirm.title}</h2>
            <p className="text-muted-foreground mb-3 text-subheadline leading-normal">
              账号「{account?.nickname ?? ''}」。{action.confirm.message}
            </p>
            {action.checklist?.length ? (
              <Card className={cn(CARD, 'mb-3 py-0')}>
                <RowList>
                  {action.checklist.map((text, index) => (
                    <SelectRow
                      key={text}
                      checked={checked[index] ?? false}
                      onCheckedChange={() => {
                        void app.haptic('light');
                        setChecked((prev) => prev.map((v, j) => (j === index ? !v : v)));
                      }}
                    >
                      {text}
                    </SelectRow>
                  ))}
                </RowList>
              </Card>
            ) : null}
            <div className="flex flex-col gap-2.5">
              <Button
                variant={action.destructive ? 'destructive' : 'default'}
                // 没勾满时保留按钮形状（默认的置灰底色和弹层背景几乎同色，看着像一行字）
                className={cn('w-full', action.destructive ? 'disabled:bg-destructive/35' : 'disabled:bg-primary/35', 'disabled:text-primary-foreground')}
                disabled={checked.some((v) => !v)}
                onClick={() => void start()}
              >
                {action.confirm.confirmLabel}
              </Button>
              <Button variant="secondary" className="w-full" onClick={() => setSheetOpen(false)}>
                取消
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </PageShell>
  );
}
